import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getResidentsCount } from "../DragItem/dragItem.utils";

/**
 * What the owner's mail says about each candidate, built here from the same
 * keys and the same rules the mail uses in rentio-api-next
 * (applicationOwnerShare.mail.ts), so the window shows the broker what they
 * are about to send rather than a description of it (Jonas, 2026-10-08).
 *
 * Written in the owner's language, not the broker's, for the same reason.
 */
export const OWNER_PREVIEW_KEY = {
  number: "email.application_to_owner.candidate.number",
  adults: "INTAKE_RESIDENT_ADULTS",
  children: "INTAKE_RESIDENT_CHILDREN",
  pets: "editor.variable.pets",
  startDate: "email.application_to_owner.candidate.start_date",
  income: "email.application_to_owner.candidate.income",
  rating: "email.application_to_owner.candidate.rating",
  comment: "email.application_to_owner.candidate.comment",
  noShow: "application.no_show.on_date",
  fileLink: "email.application_to_owner.candidate.file_link",
  // The default Settings shows for the office's own intro.
  defaultIntro: "tenant_profile.notification.owner_share_intro.placeholder",
};

const RESIDENT_ADULTS = "INTAKE_RESIDENT_ADULTS";
const RESIDENT_CHILDREN = "INTAKE_RESIDENT_CHILDREN";
const RESIDENT_PETS_PREFIX = "INTAKE_PETS";

/** The three languages a candidacy can record, by their short form. */
const RECORDED_LANGUAGES = ["nl", "fr", "en"];

// The tags Intl understands; the bundle's own locale values are not ones.
const INTL_TAG: { [locale: string]: string } = {
  [ELocale.NL]: "nl-BE",
  [ELocale.FR]: "fr-BE",
  [ELocale.EN]: "en-GB",
};

const text = (params: {
  key: string;
  locale: ELocale;
  values?: { [key: string]: string };
}) => getLocalizedText(params.key, params.values || {}, 0, params.locale);

/**
 * The language the candidacy really recorded, or nothing.
 *
 * Not `getApplicationLocale`, which falls back to Dutch for a blank column:
 * printed next to a name that default would read as something the candidate
 * said. The mail shows only a recorded one, and so does this.
 */
export const getRecordedLanguage = (application: IApplication) => {
  const stored = `${application.contacts?.[0]?.language || ""}`
    .toLowerCase()
    .trim();
  const [base] = stored.split(/[-_]/);

  return RECORDED_LANGUAGES.includes(base) ? base : undefined;
};

const countPets = (application: IApplication) =>
  (application.residents || [])
    .filter(resident => resident.id.startsWith(RESIDENT_PETS_PREFIX))
    .reduce((total, resident) => total + resident.value, 0);

/**
 * The household as labeled counts, pets only when there are any: the line
 * the mail writes (Jonas, 2026-10-08).
 */
export const getHouseholdLine = (params: {
  application: IApplication;
  locale: ELocale;
}) => {
  const { application, locale } = params;
  const residents = application.residents || [];
  const pets = countPets(application);
  const counts = [
    {
      key: OWNER_PREVIEW_KEY.adults,
      count: getResidentsCount(residents, RESIDENT_ADULTS),
    },
    {
      key: OWNER_PREVIEW_KEY.children,
      count: getResidentsCount(residents, RESIDENT_CHILDREN),
    },
    ...(pets > 0 ? [{ key: OWNER_PREVIEW_KEY.pets, count: pets }] : []),
  ];

  return counts
    .map(entry => `${text({ key: entry.key, locale })}: ${entry.count}`)
    .join(", ");
};

/** Every declared income of every contact, or nothing when none was. */
export const getHouseholdIncome = (application: IApplication) => {
  const incomes = (application.contacts || []).flatMap(contact =>
    (contact.income || []).map(entry => entry.value || 0),
  );

  return incomes.length > 0
    ? incomes.reduce((total, value) => total + value, 0)
    : undefined;
};

const formatMoney = (params: { value: number; locale: ELocale }) =>
  new Intl.NumberFormat(INTL_TAG[params.locale] || INTL_TAG[ELocale.NL], {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(params.value);

export interface IOwnerPreviewCandidate {
  id: string;
  heading: string;
  name: string;
  /** The dashed basic lines: household, start date, income. */
  lines: string[];
  /** Assessment only, like in the mail. */
  noShow?: string;
  /**
   * The broker's stars, read off the application so the render loop does not
   * have to look each candidate up again.
   */
  rating: number | null;
}

export const buildOwnerPreview = (params: {
  applications: IApplication[];
  locale: ELocale;
  includeAssessment: boolean;
}): IOwnerPreviewCandidate[] => {
  const { applications, locale, includeAssessment } = params;

  return applications.map((application, position) => {
    const contact = application.contacts?.[0];
    // First and last name only, as the mail writes it: `getName` would put a
    // company in front, which the owner never sees.
    const name = [contact?.firstname, contact?.lastname]
      .filter(Boolean)
      .join(" ");
    const language = getRecordedLanguage(application);
    const income = getHouseholdIncome(application);
    const lines = [getHouseholdLine({ application, locale })];
    // The MySQL zero date "0000-00-00" is truthy and makes date-fns throw a
    // RangeError, which would take the modal down while rendering; the mail
    // leaves it out too (see applicationIndicators.utils.ts).
    const startDate = application.dateOfOccupancy
      ? new Date(application.dateOfOccupancy)
      : null;

    if (startDate && !Number.isNaN(startDate.getTime())) {
      lines.push(
        `${text({ key: OWNER_PREVIEW_KEY.startDate, locale })}: ${formatDate(
          startDate,
        )}`,
      );
    }

    if (income !== undefined) {
      lines.push(
        `${text({ key: OWNER_PREVIEW_KEY.income, locale })}: ${formatMoney({
          value: income,
          locale,
        })}`,
      );
    }

    return {
      id: `${application.id}`,
      heading: text({
        key: OWNER_PREVIEW_KEY.number,
        locale,
        values: { number: `${position + 1}` },
      }),
      name: language ? `${name} (${language})` : name,
      lines,
      noShow:
        includeAssessment && application.noShowAt
          ? text({
              key: OWNER_PREVIEW_KEY.noShow,
              locale,
              values: { date: formatDate(application.noShowAt) },
            })
          : undefined,
      rating: application.rating ?? null,
    };
  });
};

/**
 * The intro the box starts with when the office never saved its own: the
 * default Settings shows for the owner intro. Settings only
 * displays that default, it is stored once the office saves, so without this
 * the window read nothing and opened empty (Jonas, 2026-10-08).
 *
 * The bundle text is plain with newlines and the box is the rich text editor,
 * so a blank line becomes a new paragraph and a single newline a break.
 */
export const getDefaultIntro = (locale: ELocale) =>
  text({ key: OWNER_PREVIEW_KEY.defaultIntro, locale })
    .split(/\n\s*\n/)
    .map(paragraph => `<p>${paragraph.trim().replace(/\n/g, "<br />")}</p>`)
    .join("");
